import type { WidgetApi, WidgetMountOptions, WidgetPublicState } from "@/types";
import { subscribePublicState, useWidgetStore } from "@/state";
import { getWidgetLanguage, setWidgetLanguage } from "@/core";
import { readConfigFromScript } from "@/config";
import { normalizeId } from "@/utils";
import { WidgetEvent$, type WidgetEvent } from "@/core";
import { widgetReady } from "./widgetReady";

export const onDomReady = (fn: () => void) => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
};

export const eagerMountIfPossible = async (
  mountFn: (opts?: { host?: string | HTMLElement }) => Promise<void>,
) => {
  const scriptEl = document.getElementById("md-login");
  if (!scriptEl) return;

  let cfg;
  try {
    cfg = readConfigFromScript("md-login");
  } catch {
    return;
  }

  const triggerId = normalizeId(cfg.triggerElementId);

  try {
    await mountFn();
  } catch (e) {
    console.warn("[telekomId-widget] initial mount failed", e);
    return;
  }

  if (!triggerId) return;

  const immediate = document.getElementById(triggerId);
  if (immediate) {
    if (import.meta.env.DEV || window.__KCW_DEBUG__) {
      console.debug("[telekomId-widget] trigger found immediately, rehosting");
    }
    await mountFn({ host: immediate });
    return;
  }

  const observer = new MutationObserver(() => {
    const el = document.getElementById(triggerId);
    if (!el) return;

    if (import.meta.env.DEV || window.__KCW_DEBUG__) {
      console.debug("[telekomId-widget] trigger appeared, rehosting");
    }
    observer.disconnect();
    void mountFn({ host: el });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  setTimeout(() => observer.disconnect(), 10000);
};

type InstallArgs = {
  mount: (opts?: { host?: string | HTMLElement }) => Promise<void>;
  unmount: () => void;
  openWidget: (host?: HTMLElement) => Promise<void>;
};

export const installWidgetApi = ({
  mount,
  unmount,
  openWidget,
}: InstallArgs) => {
  const existing = window.TelekomIdWidget as WidgetApi | undefined;

  const shouldReplace =
    !existing ||
    typeof existing.open !== "function" ||
    typeof existing.mount !== "function";

  if (!shouldReplace) return;

  const api: WidgetApi = {
    widgetReady,
    mount: async (opts?: WidgetMountOptions) => {
      await mount(opts?.host ? { host: opts.host } : undefined);
    },
    unmount,

    open: (opts?: WidgetMountOptions) => void openWidget(opts?.host),

    login: () => useWidgetStore.getState().actions.loginFn(),
    logout: () => useWidgetStore.getState().actions.logoutFn(),
    relogin: () => useWidgetStore.getState().actions.reLoginFn(),
    gotoProfile: () => useWidgetStore.getState().actions.gotoProfileFn(),

    setLanguage: (lang) => setWidgetLanguage(lang),
    getLanguage: () => getWidgetLanguage(),

    getState: () => useWidgetStore.getState().state,

    subscribePublicState: (cb) => subscribePublicState(cb),

    openChatbot: () => useWidgetStore.getState().actions.openChatbotFn?.(),
    continueChatbotFn: () =>
      useWidgetStore.getState().actions.continueChatbotFn?.(),
    closeChatbot: () => useWidgetStore.getState().actions.closeChatbotFn?.(),
  };

  window.TelekomIdWidget = api;

  if (import.meta.env.DEV || window.__KCW_DEBUG__) {
    (
      window.TelekomIdWidget as WidgetApi & {
        debug?: {
          getState: () => WidgetPublicState;
          subscribeEvents: (cb: (event: WidgetEvent) => void) => () => void;
        };
      }
    ).debug = {
      getState: () => useWidgetStore.getState().state,

      subscribeEvents: (cb) => {
        const sub = WidgetEvent$.subscribe(cb);
        return () => sub.unsubscribe();
      },
    };
  }
};
